'use client';

import { InputHTMLAttributes, forwardRef, useState, useCallback, ReactNode } from 'react';
import styles from './Switch.module.css';

type SwitchSize = 'sm' | 'md';
type SwitchTone = 'primary' | 'success';

interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  switchSize?: SwitchSize;
  /** On-state colour. Defaults to the brand primary; use 'success' for green on/off semantics (e.g. an SFW toggle). */
  tone?: SwitchTone;
  label?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  thumbOnIcon?: ReactNode;
  thumbOffIcon?: ReactNode;
}

const Switch = forwardRef<HTMLInputElement, SwitchProps>(({
  switchSize = 'md',
  tone = 'primary',
  label,
  checked,
  defaultChecked = false,
  thumbOnIcon,
  thumbOffIcon,
  disabled = false,
  onChange,
  className = '',
  ...props
}, ref) => {
  const [internalChecked, setInternalChecked] = useState(defaultChecked);
  const isOn = checked !== undefined ? checked : internalChecked;

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (checked === undefined) {
      setInternalChecked(e.target.checked);
    }
    onChange?.(e);
  }, [onChange, checked]);

  const wrapperClasses = [
    styles.wrapper,
    styles[switchSize],
    tone === 'success' ? styles.toneSuccess : '',
    isOn ? styles.on : '',
    disabled ? styles.disabled : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <label className={wrapperClasses}>
      <input
        ref={ref}
        type="checkbox"
        className={styles.input}
        checked={isOn}
        disabled={disabled}
        onChange={handleChange}
        role="switch"
        aria-checked={isOn}
        {...props}
      />
      <span className={styles.track}>
        <span className={styles.thumb}>
          {(thumbOnIcon || thumbOffIcon) && (
            <span className={styles.thumbIcon}>{isOn ? thumbOnIcon : thumbOffIcon}</span>
          )}
        </span>
      </span>
      {label && <span className={styles.label}>{label}</span>}
    </label>
  );
});

Switch.displayName = 'Switch';
export default Switch;
